import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { Input } from './ui/input';
import DatePickerButton from './DatePickerButton';

export const PAYMENT_METHODS = [
  { value: 'etransfer', label: 'E-transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'cash', label: 'Cash' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'other', label: 'Other' },
];

/**
 * Itemized deposits/payments tracker used in the totals sidebar of the
 * Estimate and Invoice editors. Each payment = { id, date: 'YYYY-MM-DD',
 * amount, method }. Renders rows (date + amount + method + delete) and a
 * "+ Add payment" button. Parent owns the array via onChange.
 */
export default function PaymentsEditor({ payments, onChange, accent = 'teal', testIdPrefix = 'payments' }) {
  const amber = accent === 'amber';
  const labelCls = amber ? 'text-amber-900' : 'text-[#6B7A73]';
  const boxCls = amber ? 'border-amber-200 bg-white' : 'border-[#E6EBE8] bg-white';

  const update = (idx, field, value) => {
    onChange(payments.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
  };
  const remove = (idx) => onChange(payments.filter((_, i) => i !== idx));
  const add = () =>
    onChange([
      ...payments,
      { id: `pay_${Date.now()}`, date: format(new Date(), 'yyyy-MM-dd'), amount: 0, method: 'etransfer' },
    ]);

  const fmtDate = (d) => {
    if (!d) return 'Date';
    const parsed = new Date(`${d.slice(0, 10)}T12:00:00`);
    return isNaN(parsed) ? 'Date' : format(parsed, 'MMM d, yyyy');
  };

  return (
    <div data-testid={`${testIdPrefix}-editor`}>
      <div className="flex items-center justify-between mb-1.5">
        <label className={`text-[11px] uppercase tracking-wider font-semibold ${labelCls}`}>
          Payments / Deposits
        </label>
        <button
          type="button"
          onClick={add}
          className={`flex items-center gap-1 text-[11px] font-semibold ${amber ? 'text-amber-800 hover:text-amber-950' : 'text-[#2D6B68] hover:text-[#1A1D1C]'} transition-colors`}
          data-testid={`${testIdPrefix}-add-btn`}
        >
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>

      {payments.length === 0 && (
        <p className="text-[11px] text-[#6B7A73] italic" data-testid={`${testIdPrefix}-empty`}>
          No payments recorded yet
        </p>
      )}

      <div className="space-y-2">
        {payments.map((p, idx) => (
          <div
            key={p.id || idx}
            className={`border rounded-md p-2 space-y-1.5 ${boxCls}`}
            data-testid={`${testIdPrefix}-row-${idx}`}
          >
            <div className="flex items-center gap-1.5">
              <div className="[&_button]:h-7 [&_button]:px-2 [&_button]:text-[11px] flex-1 min-w-0">
                <DatePickerButton
                  value={p.date ? `${p.date.slice(0, 10)}T12:00:00` : null}
                  label={fmtDate(p.date)}
                  onChange={(d) => update(idx, 'date', d)}
                  testId={`${testIdPrefix}-date-${idx}`}
                />
              </div>
              <div className="relative w-24 shrink-0">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-[#6B7A73]">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={p.amount === 0 ? '' : p.amount}
                  onChange={(e) => update(idx, 'amount', e.target.value === '' ? 0 : Number(e.target.value))}
                  placeholder="0.00"
                  className="h-7 pl-5 pr-1 text-right text-xs bg-white"
                  data-testid={`${testIdPrefix}-amount-${idx}`}
                />
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <select
                value={p.method || 'etransfer'}
                onChange={(e) => update(idx, 'method', e.target.value)}
                className="flex-1 h-7 text-xs border border-[#E6EBE8] rounded-md bg-white px-1.5 text-[#1A1D1C] focus:outline-none focus:border-[#2D6B68]"
                data-testid={`${testIdPrefix}-method-${idx}`}
              >
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => remove(idx)}
                className="p-1 text-[#6B7A73] hover:text-red-600 transition-colors shrink-0"
                data-testid={`${testIdPrefix}-delete-${idx}`}
                title="Remove payment"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {payments.length > 0 && (
        <div className="flex justify-between mt-2 text-xs">
          <span className={labelCls}>Total paid ({payments.length})</span>
          <span
            className="text-[#1A1D1C] font-semibold"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            data-testid={`${testIdPrefix}-total`}
          >
            −${payments.reduce((s, p) => s + (Number(p.amount) || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
      )}
    </div>
  );
}
